// ═══════════════════════════════════════════════════════════════
// DEBRIS ADMS — FEMA CLIN Rate Schedules
// Tampa-specific: CLIN codes (e.g., 1AN = AMD1 Vegetative to DMS)
// with distance-based tier pricing (0-5 mi, 5.1-10 mi, etc.)
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo, useRef } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, Select, Stack,
  TextInput, ActionIcon, Modal, SimpleGrid,
} from '../../components/gab/GabComponents';
import {
  IconPlus, IconRefresh, IconTrash, IconPencil, IconDownload, IconUpload,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useDebrisStore } from '../../store/debris-store';
import { useModulePermission } from '../../hooks/useModulePermission';
import { fetchRecords, createRecord, updateRecord, deleteRecord } from '../../api/client';
import { TABLES } from '../../api/tables';
import { FIELDS } from '../../api/fields';
import { DEBRIS_TYPE_OPTIONS } from '../../types/debris';

const FR = FIELDS.debrisRateSchedules;

const TIER_LABELS = ['0-5 mi', '5.1-10 mi', '10.1-15 mi', '15.1+ mi'];
const TIER_HEADER_BG = [
  'rgba(3,119,48,0.08)', 'rgba(75,63,255,0.08)', 'rgba(217,119,6,0.08)', 'rgba(211,52,35,0.08)',
];
const TIER_CELL_BG = [
  'rgba(3,119,48,0.04)', 'rgba(75,63,255,0.04)', 'rgba(217,119,6,0.04)', 'rgba(211,52,35,0.04)',
];

const UNIT_OPTIONS = [
  { value: 'CY', label: 'CY' },
  { value: 'Each', label: 'Each' },
  { value: 'LB', label: 'LB' },
  { value: 'Ton', label: 'Ton' },
  { value: 'Curb Mile', label: 'Curb Mile' },
];

const DEST_OPTIONS = [
  { value: 'DMS', label: 'DMS' },
  { value: 'FDS', label: 'FDS' },
  { value: 'Direct', label: 'Direct' },
];

interface RateRow {
  id: number;
  contractorId: number;
  clinCode: string;
  description: string;
  unit: string;
  rate1: number;
  rate2: number;
  rate3: number;
  rate4: number;
  amendment: string;
  notes: string;
}

function mapRate(r: Record<string, unknown>): RateRow {
  return {
    id: r.record_id as number,
    contractorId: Number(r[FR.contractorId]) || 0,
    clinCode: (r[FR.lineItemCode] as string) || '',
    description: (r[FR.description] as string) || '',
    unit: (r[FR.unit] as string) || '',
    rate1: Number(r[FR.rateTier1]) || 0,
    rate2: Number(r[FR.rateTier2]) || 0,
    rate3: Number(r[FR.rateTier3]) || 0,
    rate4: Number(r[FR.rateTier4]) || 0,
    amendment: (r[FR.category] as string) || '',
    notes: (r[FR.notes] as string) || '',
  };
}

function fmtRate(v: number): string {
  if (!isFinite(v) || v === 0) return '';
  return String(Math.round(v * 100) / 100);
}

// ── Uncontrolled rate cell for inline editing ──

function RateCell({ value, onSave, canEdit, bgColor }: {
  value: number; onSave: (v: string) => void; canEdit: boolean; bgColor: string;
}) {
  return (
    <td style={{ background: bgColor, padding: 0 }}>
      <input
        type="text"
        defaultValue={fmtRate(value)}
        onBlur={canEdit ? (e => onSave(e.target.value)) : undefined}
        readOnly={!canEdit}
        placeholder="$"
        style={{
          width: '100%', textAlign: 'right', fontSize: 12, padding: '6px 8px',
          border: 'none', background: 'transparent', outline: 'none',
          cursor: canEdit ? undefined : 'default',
        }}
      />
    </td>
  );
}

export default function RateSchedule() {
  const { canEdit } = useModulePermission('debris');
  const { contractors, loadContractors } = useDebrisStore();

  const [rates, setRates] = useState<RateRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedContractor, setSelectedContractor] = useState<string | null>(null);

  // Add/Edit CLIN modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<RateRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    clinCode: '', description: '', debrisType: '', destination: '', unit: 'CY',
    rate1: '', rate2: '', rate3: '', rate4: '', amendment: '',
  });

  // Sort
  const [clinSearch, setClinSearch] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const contractorOptions = useMemo(() =>
    contractors.map(c => ({ value: String(c.id), label: c.companyName || `#${c.id}` })),
    [contractors],
  );

  async function loadRates() {
    setLoading(true);
    try {
      const res = await fetchRecords<Record<string, unknown>>(TABLES.debrisRateSchedules, { limit: 500 });
      setRates(res.records.map(mapRate));
    } catch { /* table may not exist yet */ }
    setLoading(false);
  }

  useEffect(() => { loadContractors(); loadRates(); }, [loadContractors]);

  const contractorRates = useMemo(() => {
    if (!selectedContractor) return [];
    const cid = Number(selectedContractor);
    let rows = rates.filter(r => r.contractorId === cid);
    if (clinSearch.trim()) {
      const q = clinSearch.trim().toLowerCase();
      rows = rows.filter(r =>
        r.clinCode.toLowerCase().includes(q) ||
        r.description.toLowerCase().includes(q) ||
        r.amendment.toLowerCase().includes(q)
      );
    }
    // Apply sort
    if (sortField) {
      rows = [...rows].sort((a, b) => {
        const va = (a as any)[sortField] ?? '';
        const vb = (b as any)[sortField] ?? '';
        const n = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
        return sortDir === 'asc' ? n : -n;
      });
    } else {
      // Default sort by CLIN code
      rows = [...rows].sort((a, b) => a.clinCode.localeCompare(b.clinCode, undefined, { numeric: true }));
    }
    return rows;
  }, [rates, selectedContractor, sortField, sortDir]);

  // ── Inline rate save ──

  async function handleRateBlur(row: RateRow, field: 'rate1' | 'rate2' | 'rate3' | 'rate4', value: string) {
    const fieldMap: Record<string, string> = {
      rate1: FR.rateTier1, rate2: FR.rateTier2, rate3: FR.rateTier3, rate4: FR.rateTier4,
    };
    const numVal = parseFloat(value) || 0;
    if (numVal === row[field]) return;
    setRates(prev => prev.map(r => r.id === row.id ? { ...r, [field]: numVal } : r));
    try {
      await updateRecord(TABLES.debrisRateSchedules, row.id, { [fieldMap[field]]: numVal });
    } catch {
      notifications.show({ message: 'Failed to save rate', color: 'var(--color-error)' });
      loadRates();
    }
  }

  // ── Delete CLIN ──

  async function handleDeleteRate(row: RateRow) {
    try {
      await deleteRecord(TABLES.debrisRateSchedules, row.id);
      setRates(prev => prev.filter(r => r.id !== row.id));
    } catch {
      notifications.show({ message: 'Delete failed', color: 'var(--color-error)' });
    }
  }

  // ── Add/Edit CLIN modal ──

  function openCreate() {
    setEditTarget(null);
    setForm({ clinCode: '', description: '', debrisType: '', destination: '', unit: 'CY', rate1: '', rate2: '', rate3: '', rate4: '', amendment: '' });
    setModalOpen(true);
  }

  function openEdit(row: RateRow) {
    setEditTarget(row);
    setForm({
      clinCode: row.clinCode,
      description: row.description,
      debrisType: '', // extracted from description if needed
      destination: '',
      unit: row.unit,
      rate1: fmtRate(row.rate1), rate2: fmtRate(row.rate2),
      rate3: fmtRate(row.rate3), rate4: fmtRate(row.rate4),
      amendment: row.amendment,
    });
    setModalOpen(true);
  }

  async function handleSave() {
    if (!form.clinCode.trim()) {
      notifications.show({ message: 'CLIN code is required', color: '#C68700' });
      return;
    }
    if (!selectedContractor) {
      notifications.show({ message: 'Select a contractor first', color: '#C68700' });
      return;
    }
    if (form.description.trim().length > 0 && form.description.trim().length < 6) {
      notifications.show({ message: 'Description must be at least 6 characters', color: '#C68700' });
      return;
    }
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        [FR.contractorId]: Number(selectedContractor),
        [FR.lineItemCode]: form.clinCode.trim(),
        [FR.description]: form.description.trim(),
        [FR.unit]: form.unit,
        [FR.rateTier1]: parseFloat(form.rate1) || 0,
        [FR.rateTier2]: parseFloat(form.rate2) || 0,
        [FR.rateTier3]: parseFloat(form.rate3) || 0,
        [FR.rateTier4]: parseFloat(form.rate4) || 0,
        [FR.category]: form.amendment.trim(),
      };

      if (editTarget) {
        await updateRecord(TABLES.debrisRateSchedules, editTarget.id, payload);
        notifications.show({ message: 'CLIN updated', color: 'var(--color-success)' });
      } else {
        await createRecord(TABLES.debrisRateSchedules, payload);
        notifications.show({ message: 'CLIN added', color: 'var(--color-success)' });
      }
      await loadRates();
      setModalOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'var(--color-error)' });
    } finally { setSaving(false); }
  }

  // ── Export CSV ──

  function exportCSV() {
    if (contractorRates.length === 0) return;
    const contractor = contractors.find(c => String(c.id) === selectedContractor);
    let csv = `FEMA CLIN Rate Schedule - ${contractor?.companyName || 'Unknown'}\n`;
    csv += `CLIN Code,Description,Unit,0-5 mi,5.1-10 mi,10.1-15 mi,15.1+ mi,Amendment\n`;
    contractorRates.forEach(r => {
      csv += `"${r.clinCode}","${r.description}","${r.unit}",${r.rate1 || ''},${r.rate2 || ''},${r.rate3 || ''},${r.rate4 || ''},"${r.amendment || ''}"\n`;
    });
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clin-rates-${contractor?.companyName?.replace(/\s+/g, '-') || 'contractor'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ── Import CSV ──

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);

  async function importCSV(file: File) {
    if (!selectedContractor) return;
    setImporting(true);
    try {
      const text = await file.text();
      const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
      const dataLines = lines.slice(2); // skip title + header
      let count = 0;
      for (const line of dataLines) {
        const cols = line.match(/(".*?"|[^,]+)/g)?.map(c => c.replace(/^"|"$/g, '').trim()) ?? [];
        if (cols.length < 4) continue;
        const [code, desc, unit, t1, t2, t3, t4, amd] = cols;
        if (!code) continue;
        const existing = contractorRates.find(r => r.clinCode === code);
        const payload: Record<string, unknown> = {
          [FR.rateTier1]: parseFloat(t1) || 0,
          [FR.rateTier2]: parseFloat(t2) || 0,
          [FR.rateTier3]: parseFloat(t3) || 0,
          [FR.rateTier4]: parseFloat(t4) || 0,
        };
        if (existing) {
          await updateRecord(TABLES.debrisRateSchedules, existing.id, payload);
        } else {
          await createRecord(TABLES.debrisRateSchedules, {
            [FR.contractorId]: Number(selectedContractor),
            [FR.lineItemCode]: code,
            [FR.description]: desc || '',
            [FR.unit]: unit || 'CY',
            ...payload,
            [FR.category]: amd || '',
          });
        }
        count++;
      }
      notifications.show({ message: `Imported ${count} CLIN rows`, color: 'var(--color-success)' });
      await loadRates();
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Import failed', color: 'var(--color-error)' });
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  const contractorName = selectedContractor
    ? (contractors.find(c => String(c.id) === selectedContractor)?.companyName || '')
    : '';

  // Column defs for sortable headers
  const columns = [
    { label: 'CLIN Code', field: 'clinCode', width: 80 },
    { label: 'Description', field: 'description', width: undefined },
    { label: 'Unit', field: 'unit', width: 70 },
    { label: 'Amendment', field: 'amendment', width: 90 },
  ];

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={3} c="black">FEMA CLIN Rate Schedules</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loading} onClick={loadRates}>Refresh</Button>
          {contractorRates.length > 0 && (
            <>
              {canEdit && (
                <>
                  <input ref={fileInputRef} type="file" accept=".csv" style={{ display: 'none' }}
                    onChange={e => { const f = e.target.files?.[0]; if (f) importCSV(f); }} />
                  <Button size="xs" variant="light" leftSection={<IconUpload size={14} />}
                    loading={importing} onClick={() => fileInputRef.current?.click()}>
                    Import CSV
                  </Button>
                </>
              )}
              <Button size="xs" variant="light" leftSection={<IconDownload size={14} />} onClick={exportCSV}>
                Export CSV
              </Button>
            </>
          )}
        </Group>
      </Group>

      <Text size="sm" c="dimmed">
        Tampa FEMA CLIN rate schedules with distance-based tier pricing. Select a contractor to view or edit their rates.
      </Text>

      {/* Contractor selector */}
      <Group gap="sm" align="flex-end">
        <Select
          label="Contractor"
          data={contractorOptions}
          value={selectedContractor}
          onChange={v => setSelectedContractor(v)}
          searchable
          placeholder="Select contractor..."
          style={{ flex: 1, maxWidth: 400 }}
        />
        {canEdit && selectedContractor && (
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>
            Add CLIN
          </Button>
        )}
      </Group>

      {/* CLIN search */}
      {selectedContractor && contractorRates.length > 0 && (
        <TextInput
          placeholder="Search CLIN code, description, or amendment..."
          value={clinSearch}
          onChange={e => setClinSearch(e.target.value)}
          style={{ maxWidth: 420 }}
        />
      )}

      {/* Distance tier legend */}
      {selectedContractor && (
        <Paper p="sm" radius="md" withBorder>
          <Text size="xs" fw={600} c="dimmed" mb={4}>Distance Tiers (pickup to disposal site)</Text>
          <Group gap="md">
            {TIER_LABELS.map((label, i) => (
              <Badge key={i} size="sm" variant="light" color={['green', 'var(--color-primary-700)', '#F86A0B', 'red'][i]}>
                Tier {i + 1}: {label}
              </Badge>
            ))}
          </Group>
        </Paper>
      )}

      {/* Rate table */}
      {selectedContractor && contractorRates.length > 0 && (
        <Paper withBorder radius="md">
          <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 360px)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
              <thead>
                <tr style={{
                  borderBottom: '2px solid #cbd5e1',
                  position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc',
                }}>
                  {columns.map(col => (
                    <th
                      key={col.field}
                      onClick={() => toggleSort(col.field)}
                      style={{
                        padding: '8px 12px', textAlign: 'left', fontWeight: 600,
                        color: sortField === col.field ? '#4B3FFF' : '#64748b',
                        whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none',
                        width: col.width,
                      }}
                    >
                      {col.label}
                      <span style={{ marginLeft: 4, opacity: sortField === col.field ? 1 : 0.25 }}>
                        {sortField === col.field && sortDir === 'desc' ? '\u2193' : '\u2191'}
                      </span>
                    </th>
                  ))}
                  {TIER_LABELS.map((label, i) => (
                    <th key={i} style={{
                      padding: '6px 8px', textAlign: 'center', fontWeight: 600,
                      width: 90, background: TIER_HEADER_BG[i], color: '#64748b', fontSize: 11,
                    }}>
                      {label}
                    </th>
                  ))}
                  {canEdit && <th style={{ width: 60 }}></th>}
                </tr>
              </thead>
              <tbody>
                {contractorRates.map(row => (
                  <tr key={row.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '6px 12px', fontWeight: 600 }}>{row.clinCode}</td>
                    <td style={{ padding: '6px 12px' }}>
                      <Text size="xs">{row.description}</Text>
                    </td>
                    <td style={{ padding: '6px 12px' }}>
                      <Badge size="xs" variant="light">{row.unit}</Badge>
                    </td>
                    <td style={{ padding: '6px 12px' }}>
                      {row.amendment && (
                        <Badge size="xs" variant="light" color="#6366f1">{row.amendment}</Badge>
                      )}
                    </td>
                    {(['rate1', 'rate2', 'rate3', 'rate4'] as const).map((field, i) => (
                      <RateCell
                        key={`${field}-${row[field]}`}
                        value={row[field]}
                        canEdit={canEdit}
                        bgColor={TIER_CELL_BG[i]}
                        onSave={v => handleRateBlur(row, field, v)}
                      />
                    ))}
                    {canEdit && (
                      <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                        <Group gap={2} justify="flex-end">
                          <ActionIcon size="xs" variant="subtle" onClick={() => openEdit(row)}>
                            <IconPencil size={12} />
                          </ActionIcon>
                          <ActionIcon size="xs" variant="subtle" color="var(--color-error)" onClick={() => handleDeleteRate(row)}>
                            <IconTrash size={12} />
                          </ActionIcon>
                        </Group>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ padding: '8px 12px', borderTop: '1px solid #e2e8f0', background: '#f8fafc' }}>
            <Text size="xs" c="dimmed">
            {clinSearch.trim()
              ? `${contractorRates.length} of ${rates.filter(r => r.contractorId === Number(selectedContractor)).length} CLINs matching "${clinSearch.trim()}"`
              : `${contractorRates.length} CLIN${contractorRates.length !== 1 ? 's' : ''} for ${contractorName}`
            }
          </Text>
          </div>
        </Paper>
      )}

      {selectedContractor && contractorRates.length === 0 && !loading && (
        <Paper p="xl" radius="md" withBorder style={{ textAlign: 'center' }}>
          <Text c="dimmed">No CLIN rates for {contractorName}.</Text>
          {canEdit && (
            <Button mt="sm" size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>
              Add First CLIN
            </Button>
          )}
        </Paper>
      )}

      {!selectedContractor && (
        <Paper p="xl" radius="md" withBorder style={{ textAlign: 'center' }}>
          <Text c="dimmed">Select a contractor to view or manage their CLIN rate schedule.</Text>
        </Paper>
      )}

      {/* ── Add / Edit CLIN Modal ── */}
      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit CLIN' : 'Add CLIN'}
        centered
        size="lg"
      >
        <Stack gap="md">
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TextInput
              label="CLIN Code"
              required
              placeholder="e.g. 1AN"
              value={form.clinCode}
              onChange={e => setForm(f => ({ ...f, clinCode: e.target.value }))}
            />
            <TextInput
              label="Amendment Reference"
              placeholder="e.g. AMD1 or blank for base"
              value={form.amendment}
              onChange={e => setForm(f => ({ ...f, amendment: e.target.value }))}
            />
          </SimpleGrid>

          <TextInput
            label="Description"
            placeholder="e.g. AMD1 Vegetative Debris to DMS"
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            error={form.description.trim().length > 0 && form.description.trim().length < 6 ? 'Minimum 6 characters' : undefined}
          />

          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
            <Select
              label="Debris Type"
              data={DEBRIS_TYPE_OPTIONS}
              value={form.debrisType || null}
              onChange={v => setForm(f => ({ ...f, debrisType: v ?? '' }))}
              clearable
              placeholder="Optional"
            />
            <Select
              label="Destination Type"
              data={DEST_OPTIONS}
              value={form.destination || null}
              onChange={v => setForm(f => ({ ...f, destination: v ?? '' }))}
              clearable
              placeholder="DMS, FDS, Direct"
            />
            <Select
              label="Unit"
              data={UNIT_OPTIONS}
              value={form.unit}
              onChange={v => setForm(f => ({ ...f, unit: v ?? 'CY' }))}
            />
          </SimpleGrid>

          <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
            <Text size="xs" fw={600} c="dimmed" mb={8}>Distance Tier Rates</Text>
            <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
              {TIER_LABELS.map((label, i) => {
                const fieldKey = `rate${i + 1}` as 'rate1' | 'rate2' | 'rate3' | 'rate4';
                return (
                  <TextInput
                    key={i}
                    label={label}
                    type="number"
                    step="0.01"
                    placeholder="$0.00"
                    value={form[fieldKey]}
                    onChange={e => setForm(f => ({ ...f, [fieldKey]: e.target.value }))}
                  />
                );
              })}
            </SimpleGrid>
          </div>

          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSave}>
              {editTarget ? 'Save Changes' : 'Add CLIN'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
